import {countryIds, VALID_COUNTRY_IDS, languageIds, languages, defaultLocaleId, defaultCountryId} from '@local/config/locales.mjs'
import {locale as currentLocale} from '@local/stores/dictionaries/route'

// string utils to manage Sanity queries
export const parseLocale = {
	dashToUnderscore: (str: string): string => str.replaceAll('-', '_'),
	underscoreToDash: (str: string): string => str.replaceAll('_', '-'),
	language: (str: string): string => str.split('-')[0],
	country: (str: string): string => str.split('-')[1],
}

// convert locale string to array, with optional casing:
// 'en-us' -> ['en', 'us']
export const getLocaleDataAsArray = (str: string, {uppercase = false} = {}) => {
	return str[uppercase ? 'toUpperCase' : 'toLowerCase']().split('-')
}

// convert locale string to object, with optional casing
// 'en-us' -> { language: 'en', countryCode: 'us' }
export const getLocaleData = (str: string, options = {uppercase: false}) => {
	const [language, countryCode] = getLocaleDataAsArray(str, options)
	return {
		language,
		countryCode,
	}
}

/** validate whether a string is a valid localeId */
export const isValidLocale = (str: string, options?: {validCountries: string[]}): boolean => {
	const [maybeLanguage, maybeCountryCode] = getLocaleDataAsArray(str)
	const isValidLanguage = languageIds.includes(maybeLanguage)
	const isValidCountryCode = countryIds.includes(maybeCountryCode) || (options?.validCountries || VALID_COUNTRY_IDS).includes(maybeCountryCode)
	return isValidLanguage && isValidCountryCode
}

// Given a locale, find the default countryCode for that language
/**
 * Assuming:
 * - EN is the default language, and NL is the default countryCode for EN
 * - FR is another language, and FR is the default countryCode for FR
 *
 * Given:
 * en-us -> en-nl
 * fr-nl -> fr-fr
 *
 * This helps using the current language, when the countrycode is not matching,
 * ie, given fr-nl, we can fallback to `fr-fr` first, before falling back to `en-nl`
 */
export const getFallbackLocale = (locale: string) => {
	const {language} = getLocaleData(locale)
	const defaultCountryForLanguage = languages.find(lang => lang.id === language)?.defaultCountry || defaultCountryId

	if (!defaultCountryForLanguage) {
		// default country should exist for all languages!
		console.warn('[Error]: Default Country not set for', language)
	}

	return `${language}-${defaultCountryForLanguage}`
}

// string utils to use in Sanity queries
export const getParsedLocales = (locale: string) => {
	const fallback = parseLocale.dashToUnderscore(defaultLocaleId)
	locale = locale || currentLocale.current

	return {
		// current server locale
		current: parseLocale.dashToUnderscore(locale),
		// fallback to default countryCode for language
		currentFallback: parseLocale.dashToUnderscore(getFallbackLocale(parseLocale.underscoreToDash(locale))),
		// global fallback to default locale
		fallback,
	}
}
